import { connector } from './ApiAndCacheConnector';
import CacheRequest from './CacheRequest';
import { toggleGlobalLoader } from 'qs-helpers/index';
import {
  setCatalogueTagVisibilityInNative,
  saveSortedTagsInNative,
  saveTagsChangesInNative
} from 'qs-data-manager/Dexie/CatalogueDexieHelpers';
import Api from 'qs-services/Api';
import CatalogueLib from 'qs-data-manager/Catalogues';
import Mixpanel from './Mixpanel';
import * as Sentry from '@sentry/browser';
import { registerCleanupHandler } from 'qs-helpers/ClearSavedData';

let bulkTagsResp = null;

const setCatalogueTagVisibilityInLocal = ({ catalogueId, tag, v }) => {
  const key = `${connector.CATALOGUE_TAGS.cacheKey}${catalogueId}`;
  const { tags: tagsCache } = CacheRequest.getCacheForKey(key);
  tagsCache[tag] = { ...tagsCache[tag], v };
  const newTagsCache = { ...tagsCache };

  CacheRequest.setCacheForKey(key, { tags: newTagsCache });
  return {
    newTagsCache
  };
};

const setCatalogueTagVisibilityInRemote = async ({ catalogueId, tag, v }) => {
  return Api.setCatalogueTagVisibility({ catalogueId, tagId: tag, visibility: v });
};

const setCatalogueTagVisibility = async ({ catalogueId, tag, v }) => {
  const loaderId = `${catalogueId}${tag}`;
  try {
    toggleGlobalLoader(loaderId, true);
    const changes = setCatalogueTagVisibilityInLocal({ catalogueId, tag, v });
    await Promise.all([
      setCatalogueTagVisibilityInRemote({ catalogueId, tag, v }),
      setCatalogueTagVisibilityInNative({ catalogueId, changes })
    ]);

    toggleGlobalLoader(loaderId, false);
  } catch (error) {
    toggleGlobalLoader(loaderId, false);
    Sentry.captureException(error);
  }
};

const sortTagsInLocal = ({ catalogueId, modifiedTags }) => {
  const key = `${connector.CATALOGUE_TAGS.cacheKey}${catalogueId}`;
  const oldCache = CacheRequest.getCacheForKey(key) || {};

  const userTags = {};
  modifiedTags.forEach((tag, index) => {
    userTags[tag.title] = { ...tag, p: index };
  });

  const newTagsCache = { ...(oldCache.tags || {}), ...userTags };

  CacheRequest.setCacheForKey(key, { tags: newTagsCache });
  return {
    newTagsCache
  };
};

const sortTagsInRemote = async ({ catalogueId, tags }) => {
  const reorderedTags = tags.map((tag, index) => ({ tagId: tag.title, position: index }));
  await Api.reorderCatalogueTags({ catalogueId, reorderedTags });
};

const filterStockTags = tags => {
  const filteredTags = [];
  const tagsRemoved = [];

  tags.forEach(tag => {
    if (tag.title !== 'Out of stock' && tag.title !== 'In stock') {
      filteredTags.push(tag);
    } else {
      tagsRemoved.push(tag);
    }
  });

  return {
    filteredTags,
    tagsRemoved
  };
};

const sortCatalogueTags = async ({ catalogueId, tags }) => {
  const key = `sortCatalogueTags${catalogueId}${Date.now()}`;
  try {
    toggleGlobalLoader(key, true);

    const { filteredTags } = filterStockTags(tags);

    const changes = sortTagsInLocal({ catalogueId, modifiedTags: filteredTags });

    await Promise.all([
      sortTagsInRemote({ catalogueId, tags: filteredTags }),
      saveSortedTagsInNative({ catalogueId, changes })
    ]);

    Mixpanel.sendEvent({
      eventName: 'catalogue_tags_reordered',
      props: {
        catalogueId
      }
    });

    toggleGlobalLoader(key, false);
  } catch (error) {
    Sentry.captureException(error);
  }
};

const onChangeCatalogueTagsFromRemote = ({ tags, catalogueId }) => {
  const key = `${connector.CATALOGUE_TAGS.cacheKey}${catalogueId}`;
  CacheRequest.setCacheForKey(key, { tags });
  saveTagsChangesInNative({ catalogueId, tags });
};

const isTagValid = tag => !/.*[/.#$[\]].*/.test(tag);

const addProductTag = async ({ productId, tagId, position, catalogueId }) => {
  const loaderKey = `addProductTag${Date.now()}`;
  toggleGlobalLoader(loaderKey, true);

  const basicInfoKey = `${connector.BASIC_INFO.cacheKey}${productId}`;
  const catalogueTagKey = `${connector.CATALOGUE_TAGS.cacheKey}${catalogueId}`;

  const basicInfo = CacheRequest.getCacheForKey(basicInfoKey);
  const catalogueTags = CacheRequest.getCacheForKey(catalogueTagKey) || {};

  const catalogueTagsLength = Object.keys(catalogueTags.tags || {}).length;

  const productTag = {
    title: tagId,
    v: true,
    p: position,
    t: Date.now()
  };

  const newTags = { ...(basicInfo.tags || []), [tagId]: productTag };
  const newCache = { ...basicInfo, tags: newTags };

  if (!catalogueTags || !catalogueTags.tags || !catalogueTags.tags[tagId]) {
    const newCatalogueTag = {
      title: tagId,
      v: true,
      p: catalogueTagsLength,
      t: Date.now()
    };

    const newCatalogueTagsCache = { ...(catalogueTags.tags || {}), [tagId]: newCatalogueTag };
    CacheRequest.setCacheForKey(catalogueTagKey, { tags: newCatalogueTagsCache });
  }

  CacheRequest.setCacheForKey(basicInfoKey, newCache);
  await Api.createTagInProduct(catalogueId, [productId], tagId);

  Mixpanel.sendEvent({
    eventName: 'product_tag_added',
    props: {
      tagTitle: tagId,
      productId
    }
  });
  toggleGlobalLoader(loaderKey, false);
};

const addProductsTag = async ({ catalogueId, productIds, tag }) => {
  const loaderKey = `addProductsTag${Date.now()}`;
  try {
    toggleGlobalLoader(loaderKey, true);

    const basicInfoShareKey = connector.BASIC_INFO.cacheKey;
    const catalogueTagsKey = `${connector.CATALOGUE_TAGS.cacheKey}${catalogueId}`;

    const newTag = {
      title: tag,
      v: true,
      t: Date.now()
    };

    productIds.forEach(id => {
      const key = `${basicInfoShareKey}${id}`;
      const basicInfo = CacheRequest.getCacheForKey(key) || {};
      const largestPosition = Object.values(basicInfo.tags || {}).reduce(
        (acc, val) => (val.p > acc ? val.p : acc),
        -1
      );
      const localTag = { ...newTag, p: largestPosition + 1 };
      const newBasicInfo = { ...basicInfo, tags: { ...basicInfo.tags, [tag]: localTag } };
      CacheRequest.setCacheForKey(key, newBasicInfo);
    });

    const catalogueTags = CacheRequest.getCacheForKey(catalogueTagsKey) || {};

    if (!catalogueTags || !catalogueTags.tags || !catalogueTags.tags[tag]) {
      const catalogueTag = Object.values(catalogueTags.tags || {}).reduce(
        (acc, val) => (val.p > acc.p ? val : acc),
        {}
      );
      const position = (catalogueTag.p || 0) + 1;
      const localTag = {
        title: tag,
        v: true,
        t: Date.now(),
        p: position
      };
      CacheRequest.setCacheForKey(catalogueTagsKey, {
        tags: { [tag]: localTag, ...(catalogueTags.tags || {}) }
      });
    }
    await Api.createTagInProduct(catalogueId, productIds, tag);

    Mixpanel.sendEvent({
      eventName: 'bulk_product_tag_added',
      props: {
        tagTitle: tag,
        total_product_count: productIds.length
      }
    });
    toggleGlobalLoader(loaderKey, false);
  } catch (error) {
    toggleGlobalLoader(loaderKey, false);
    Sentry.captureException(error);
  }
};

// Does not delete tag from remote, a separate function does that
const deleteProductTag = ({ catalogueId, productId, tag, unselectedTags }) => {
  const loaderKey = `deleteProductTag${Date.now()}`;
  try {
    toggleGlobalLoader(loaderKey, true);

    const selectedTagsKey = `${connector.BASIC_INFO.cacheKey}${productId}`;
    const newUnselectedTags = [...unselectedTags];

    const basicInfo = CacheRequest.getCacheForKey(selectedTagsKey);

    if (basicInfo && basicInfo.tags && basicInfo.tags[tag]) {
      delete basicInfo.tags[tag];
    }

    const newSelectedTags = Object.values(basicInfo.tags)
      .sort((t1, t2) => t1.p - t2.p)
      .map(tag => tag.title);

    newUnselectedTags.push(tag);
    CacheRequest.setCacheForKey(selectedTagsKey, basicInfo);

    toggleGlobalLoader(loaderKey, false);
    return {
      selectedTags: newSelectedTags,
      newUnselectedTags
    };
  } catch (err) {
    toggleGlobalLoader(loaderKey, false);
    Sentry.captureException(err);
  }
};

const deleteProductTagInRemote = async ({ catalogueId, productIds, tagId }) => {
  const loaderKey = `deleteProductTagInRemote${Date.now()}`;
  try {
    toggleGlobalLoader(loaderKey, true);

    await Api.deleteProductTag({ productIds, tagId });
    CatalogueLib.getCatalogueTags(catalogueId);

    if (productIds.length === 1) {
      Mixpanel.sendEvent({
        eventName: 'product_tag_deleted',
        props: {
          productId: productIds[0],
          tagTitle: tagId
        }
      });
    } else {
      Mixpanel.sendEvent({
        eventName: 'bulk_product_tag_deleted',
        props: {
          productIds,
          tagTitle: tagId
        }
      });
    }
    toggleGlobalLoader(loaderKey, false);
  } catch (err) {
    toggleGlobalLoader(loaderKey, false);
    Sentry.captureException(err);
  }
};

const sortProductTags = async ({ productId, tagsIds }) => {
  const loaderKey = `sortProductTags${Date.now()}`;
  try {
    toggleGlobalLoader(loaderKey, true);

    const key = `${connector.BASIC_INFO.cacheKey}${productId}`;
    const basicInfo = CacheRequest.getCacheForKey(key);
    const reorderedTags = {};
    const apiData = [];

    tagsIds.forEach((tagId, index) => {
      const tag = basicInfo.tags[tagId];
      reorderedTags[tag.title] = { ...tag, p: index };
      apiData.push({ tagId, position: index });
    });

    CacheRequest.setCacheForKey(key, { ...basicInfo, tags: reorderedTags });
    await Api.reorderTags({
      productId,
      reorderedTags: apiData
    });

    Mixpanel.sendEvent({
      eventName: 'product_tags_reordered',
      props: {
        productId
      }
    });

    toggleGlobalLoader(loaderKey, false);
  } catch (error) {
    toggleGlobalLoader(loaderKey, false);
    Sentry.captureException(error);
  }
};

const deleteProductsTag = ({ catalogueId, productIds, tag }) => {
  const loaderKey = `deleteProductsTag${Date.now()}`;

  try {
    toggleGlobalLoader(loaderKey, true);
    productIds.forEach(id => {
      const key = `${connector.BASIC_INFO.cacheKey}${id}`;
      const basicInfo = CacheRequest.getCacheForKey(key);

      if (basicInfo && basicInfo.tags && basicInfo.tags[tag]) {
        delete basicInfo.tags[tag];
      }

      CacheRequest.setCacheForKey(key, basicInfo);
    });

    setTimeout(() => {
      Api.getCatalogueTags(catalogueId);
    }, 1500);
  } catch (error) {
    Sentry.captureException(error);
  }

  toggleGlobalLoader(loaderKey, false);
};

const fetchCommonBulkTags = async ({ productIds }) => {
  try {
    const key = `${JSON.stringify(productIds)}`;
    bulkTagsResp = key;

    const { tags } = await Api.getBulkProductTags(productIds);
    const exists = (() => bulkTagsResp === key)(); // Ensures that prev resp does not overwrite the new resp

    if (!exists) {
      return;
    }

    const selectedCommonTags = Object.values(tags).map(tag => tag.title);
    bulkTagsResp = null;

    return selectedCommonTags;
  } catch (error) {
    bulkTagsResp = null;
    Sentry.captureException(error);
  }
};

const computeUnSelectedFromSelected = (catalogueId, selectedCommonTags = []) => {
  const key = `${connector.CATALOGUE_TAGS.cacheKey}${catalogueId}`;
  const cache = CacheRequest.getCacheForKey(key) || {};
  const allTags = Object.keys(cache.tags || {});

  const unselected = [];

  allTags.forEach(tag => {
    if (selectedCommonTags.indexOf(tag) < 0 && tag !== 'Out of stock' && tag !== 'In stock') {
      unselected.push(tag);
    }
  });

  return unselected;
};

const clearTagsSavedData = () => {
  bulkTagsResp = null;
};

registerCleanupHandler(clearTagsSavedData);

export default {
  setCatalogueTagVisibility,
  sortCatalogueTags,
  onChangeCatalogueTagsFromRemote,
  isTagValid,
  addProductTag,
  addProductsTag,
  deleteProductTag,
  deleteProductsTag,
  sortProductTags,
  deleteProductTagInRemote,
  fetchCommonBulkTags,
  computeUnSelectedFromSelected,
  filterStockTags
};
